<template>
  <uikit-drawer id="drawer-manage-tub-tags" title="Gérer les tags de l'annonce">
    <uikit-form-row one-column>
      <uikit-form-cell title="Tags">
        <uikit-autocomplete-new
          id="tag"
          v-model="tagsSearchQuery"
          name="tag"
          class="locations-selector"
          standalone
          placeholder="Rechercher..."
          :options="tagsSearchOptions"
          :loading="tagsSearchLoading"
          @update:model-value="handleUpdateTag"
          @on-select-option="handleSelectTag"
        >
          <template v-if="tags.length" #prefix>
            <uikit-tag-list :tags="tags" class="location-selector">
              <template #tag="{ tag }">
                <uikit-close-icon class="cross-icon" @click.native="handleRemoveTag($event, tag)" />
                <span class="separator">|</span>
                {{ tagFormatter(tag) }}
              </template>
            </uikit-tag-list>
          </template>

          <template #option="option">
            {{ tagFormatter(option) }}
          </template>

          <template #suffix>
            <uikit-button class="add-tag" type="link" @click="handleAddNewTag">
              <template #icon>
                <uikit-add-link-icon />
              </template>
            </uikit-button>
          </template>
        </uikit-autocomplete-new>
      </uikit-form-cell>
    </uikit-form-row>

    <template #footer>
      <uikit-button type="link" :loading="isSubmitting" :disabled="isSubmitting" @click="handleCancel">
        Annuler
      </uikit-button>
      <uikit-button
        class="main-cta"
        type="primary"
        :loading="isSubmitting"
        :disabled="isSubmitting"
        :inactive="!meta.valid || isSubmitting"
        @click="handleValidate"
      >
        Valider
      </uikit-button>
    </template>
  </uikit-drawer>
</template>

<script setup>
import {
  UikitAutocompleteNew,
  UikitButton,
  UikitCloseIcon,
  UikitDrawer,
  UikitFormCell,
  UikitFormRow,
  UikitTagList,
  UikitAddLinkIcon,
  useDrawerStore,
} from '@hz/ui-kit'

import { useForm } from 'vee-validate'

const props = defineProps({
  realEstateOfferId: {
    type: String,
    required: true,
  },
})

const attrs = useAttrs()

const tagsSearchQuery = ref('')
const tagsSearchOptions = ref([])
const tagsSearchLoading = ref(false)

const validationSchema = {
  tags: {},
}

const { handleSubmit, isSubmitting, values, setFieldValue, meta } = useForm({
  validationSchema,
  initialValues: {
    tags: [],
  },
})

const tags = computed(() => {
  if (!values.tags) {
    return []
  }

  return values.tags.map((tag) => ({
    type: 'default',
    ...tag,
  }))
})

const tagFormatter = (option) => {
  return `${option.name}`
}

const areSameTags = (tagA, tagB) => {
  return tagA.name === tagB.name
}

const handleAddNewTag = () => {
  setFieldValue('tags', [...(values.tags ?? []), { name: tagsSearchQuery.value, isNew: true }])
  tagsSearchQuery.value = ''
}

const handleUpdateTag = async (query) => {
  if (!query) {
    tagsSearchOptions.value = []

    return
  }
  tagsSearchLoading.value = true
  const results = await useAdsApi().searchTags({
    name: query,
  })
  tagsSearchOptions.value = results.data
    .map((result) => ({
      name: result.name,
      isNew: false,
    }))
    .filter((result) => !values.tags?.some((l) => areSameTags(l, result)))
  tagsSearchLoading.value = false
}

const handleSelectTag = (tag) => {
  if (values.tags?.find((l) => areSameTags(l, tag))) {
    return
  }

  setFieldValue('tags', [...(values.tags ?? []), tag])
}

const handleRemoveTag = (e, tag) => {
  e.preventDefault()
  e.stopPropagation()

  setFieldValue('tags', !values.tags ? [] : values.tags.filter((l) => !areSameTags(l, tag)))
}

const close = () => {
  useDrawerStore().removeDrawer({ id: attrs.id })
}

const handleValidate = handleSubmit(async (values) => {
  const { tags } = values
  await useAdsApi().updateOfferById(props.realEstateOfferId, { tags: tags.map((tag) => tag.name) })
  close()
})

const handleCancel = () => {
  close()
}

onMounted(async () => {
  const offer = await useAdsApi().getAnnounceById(props.realEstateOfferId)
  setFieldValue(
    'tags',
    offer.tags.map((tag) => ({
      name: tag,
      isNew: false,
    })) ?? [],
  )
})
</script>

<style lang="less" scoped>
.location-selector {
  .cross-icon {
    width: 9px;
    height: 9px;
    margin-top: 1px;
    margin-left: 6px;
    align-self: center;
    color: var(--ds-color-primary-100);
  }
  .separator {
    margin-left: 4px;
    margin-right: 4px;
    color: var(--ds-color-primary-25);
  }
}

:deep(.locations-selector) {
  > .uikit-input-wrapper {
    height: auto;
    min-height: 44px;
    padding: 4px 0 4px 12px;
    flex-wrap: wrap;

    > .prefix {
      max-width: 100%;
      flex-grow: 1;

      @media @bp-md-down {
        width: 100%;
      }
    }

    > input {
      min-width: 120px;
      width: auto;
      flex-grow: 1;
      height: 18px;
      margin-top: 8px;
      margin-bottom: 8px;
    }

    > .suffix {
      width: 38px;

      svg {
        margin-left: 3px;
      }

      > .separator {
        margin-left: 4px;
        margin-right: 4px;
        background-color: var(--ds-color-gray-10);
        height: 80%;
        width: 1px;
      }
    }
  }
}

:deep(.city-range-selector) {
  width: 140px;

  .uikit-input-wrapper {
    border: none;
    height: 34px;

    > input {
      color: var(--ds-color-primary-100);
      font-weight: var(--ds-weight-semi-bold);
      height: 18px;
      margin-top: 8px;
      margin-bottom: 8px;
    }
  }
}

:deep(.drawer-footer) {
  display: flex;
  flex-direction: row;

  .main-cta {
    flex-grow: 1;
  }
}

:deep(.u-gap-8) {
  gap: 8px;
}

.cta-separator {
  margin-left: 8px;
  margin-right: 8px;
  width: 1px;
  height: 24px;
  vertical-align: middle;
  background-color: var(--ds-color-gray-25);
  display: inline-block;
}

:deep(.add-tag) {
  height: auto;
  > span {
    display: flex;
  }
}
</style>
